import { getConfig } from '@edx/frontend-platform';
import { logError } from '@edx/frontend-platform/logging';

import type { CourseData, SearchCourseDiscoveryResults } from '../types';
import { apiRequest } from './utils/apiClient';

export const courseMetadataApi = {
  /**
   * Fetches course search results based on language code
   * @param {string} languageCode - ISO 639-1 language code for course content localization.
   *   Supported values: "en" (English), "es" (Spanish), "fr" (French), "hi" (Hindi), "sw" (Swahili), "zh" (Chinese)
   * @param {Function} callback - Callback function that handles the search results.
   *   The function receives SearchCourseDiscoveryResults as its parameter.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  getCourseSearch: async (
    languageCode: string,
    callback: (res: SearchCourseDiscoveryResults) => void,
  ): Promise<void> => {
    const searchData = `search_string=&page_size=100&page_index=0&language=${languageCode}`;
    const url = `${getConfig().LMS_BASE_URL}/search/course_discovery/`;

    try {
      const data = await apiRequest<SearchCourseDiscoveryResults>({
        url,
        isAuth: true,
        method: 'POST',
        data: searchData,
        contentType: 'application/x-www-form-urlencoded;charset=UTF-8',
      });

      callback(data);
    } catch (error) {
      logError('Error fetching course search', error);
    }
  },

  getCourseMeta: async (
    courseID: string,
    callback: (res: CourseData) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    const url = `${getConfig().LMS_BASE_URL}/api/course-info/${courseID}/`;

    try {
      const data = await apiRequest<CourseData>({
        url,
        isAuth: false,
        method: 'GET',
      });
      callback(data);
    } catch (error) {
      logError('Fetch course meta failed', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },
};
